import React, { useEffect, useRef } from 'react';
import { Users, Award, Camera, Heart } from 'lucide-react';

const About = () => {
  const aboutRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-fade-in');
        }
      },
      { threshold: 0.1 }
    );

    if (aboutRef.current) {
      observer.observe(aboutRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const stats = [
    { icon: Camera, number: '50+', label: 'Proyek Selesai' },
    { icon: Heart, number: '50+', label: 'Klien Puas' },
    { icon: Users, number: '5+', label: 'Tim Profesional' },
    { icon: Award, number: '2+', label: 'Tahun Pengalaman' },
  ];

  return (
    <section 
      id="about" 
      ref={aboutRef}
      className="py-20 lg:py-32 bg-white dark:bg-gray-900 opacity-0 transition-all duration-1000"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Content */}
          <div className="space-y-8">
            <div>
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-6">
                Tentang Creativa
              </h2>
              <p className="text-xl text-gray-600 dark:text-gray-300 leading-relaxed mb-6">
                Kami adalah tim kreatif yang berdedikasi untuk menciptakan karya visual yang berkesan dan bermakna. 
                Dengan pengalaman bertahun-tahun di industri kreatif, kami memahami pentingnya setiap detail dalam 
                menceritakan kisah melalui lensa.
              </p>
              
              <p className="text-lg text-gray-600 dark:text-gray-400 leading-relaxed mb-8">
                Dari fotografi produk hingga dokumenter pernikahan, dari video korporat hingga konten sosial media, 
                kami menghadirkan solusi visual yang tepat untuk setiap kebutuhan klien. Setiap proyek adalah 
                kesempatan bagi kami untuk menciptakan sesuatu yang luar biasa.
              </p>
              
              <div className="flex flex-wrap gap-4">
                <span className="px-4 py-2 bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white rounded-full text-sm font-medium">
                  Kreatif
                </span>
                <span className="px-4 py-2 bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white rounded-full text-sm font-medium">
                  Profesional
                </span>
                <span className="px-4 py-2 bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white rounded-full text-sm font-medium">
                  Berpengalaman
                </span>
                <span className="px-4 py-2 bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white rounded-full text-sm font-medium">
                  Terpercaya
                </span>
              </div>
            </div>
          </div>
          
          {/* Image */}
          <div className="relative">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?ixlib=rb-4.0.3&auto=format&fit=crop&w=2071&q=80" 
                alt="Tim Creativa"
                className="w-full h-96 lg:h-[500px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
            </div>
            
            {/* Floating card */}
            <div className="absolute -bottom-8 -left-8 bg-white dark:bg-gray-800 p-6 rounded-xl shadow-xl max-w-xs">
              <h3 className="font-bold text-gray-900 dark:text-white mb-2">
                Misi Kami
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Menghadirkan karya visual yang tidak hanya indah dipandang, tetapi juga mampu menyentuh hati dan menyampaikan pesan dengan kuat.
              </p>
            </div>
          </div>
        </div>
        
        {/* Stats */}
        <div className="mt-20 pt-16 border-t border-gray-200 dark:border-gray-700">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => {
              const IconComponent = stat.icon;
              return (
                <div key={index} className="text-center group">
                  <div className="w-16 h-16 bg-gray-900 dark:bg-white rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                    <IconComponent size={32} className="text-white dark:text-gray-900" />
                  </div>
                  <div className="text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white mb-2">
                    {stat.number}
                  </div>
                  <div className="text-gray-600 dark:text-gray-400 font-medium">
                    {stat.label}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;